import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, useReducedMotion, type Variants } from 'framer-motion'
import { storeConfig } from '../../../../store.config'
import { ROUTES } from '../../../constants/routes.constants'
import { useAuth } from '../../../context/AuthContext'
import { useCartContext } from '../../../context/CartContext'
import { useProfileContext } from '../../../context/ProfileContext'
import { useTenant } from '../../../context/TenantContext'
import { CustomerSidebar } from '../layouts/CustomerSidebar'

/**
 * Shared storefront chrome — the public (signed-out) look-and-feel used by
 * the home page and the story page. Keeps both pages DRY (Rule #21) and
 * consistent. Colours come from design tokens (Rule #19); the grain is a
 * pure technique (no brand colour).
 */

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]

/** Soft fade-up; collapses to a plain fade when reduced-motion is on. */
export function useFadeUp(): Variants {
  const reduce = useReducedMotion()
  return {
    hidden: { opacity: 0, y: reduce === true ? 0 : 24 },
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, delay: (i as number) * 0.1, ease: EASE }
    })
  }
}

/** Subtle film grain (Nature Distilled). */
export function GrainOverlay(): JSX.Element {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-multiply"
      style={{
        backgroundSize: '170px 170px',
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='170' height='170'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
      }}
    />
  )
}

/** Shared full-bleed warm backdrop — the brand photo under a warm token
 *  scrim tuned for legible DARK text, or the atmospheric CSS wash when no
 *  photo is configured. Used by the hero AND the story overlay. */
export function StorefrontBackdrop({ drift = false }: { readonly drift?: boolean } = {}): JSX.Element {
  const hero = storeConfig.brand.heroImagePath
  const reduce = useReducedMotion() === true
  const animateDrift = drift && !reduce
  return (
    <div aria-hidden className="absolute inset-0 z-0 overflow-hidden">
      {hero !== null ? (
        <>
          {/* Blurred photo under a warm cream scrim = a soft creamish backdrop
              (Kiran 2026-05-25). The current hero.jpg is B&W, so the cream wash
              is what warms it from cold-grey to creamish; drop a COLOUR photo at
              brand.heroImagePath to make it colourful (no code change needed).
              `drift` (home route only) adds a slow Ken-Burns scale/pan; off
              elsewhere + under reduced-motion. */}
          {animateDrift ? (
            <motion.img
              src={hero}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{
                objectPosition: '50% 22%',
                transformOrigin: 'left center',
                filter: 'blur(5px)'
              }}
              initial={{ scale: 1.22, x: 0, y: 0 }}
              animate={{ scale: [1.22, 1.3, 1.22], x: ['0%', '-1.5%', '0%'], y: ['0%', '-1.2%', '0%'] }}
              transition={{ duration: 34, ease: 'easeInOut', repeat: Infinity }}
            />
          ) : (
            <img
              src={hero}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{
                objectPosition: '50% 22%',
                transform: 'scale(1.22)',
                transformOrigin: 'left center',
                filter: 'blur(5px)'
              }}
            />
          )}
          {/* Light cream tint — keep it faint so the grey image shows through;
              hero/story copy sits on its own frosted panel for legibility. */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(165deg, var(--color-bg-primary), var(--color-bg-secondary) 100%)',
              opacity: 0.3
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(82% 72% at 50% 45%, var(--color-bg-primary) 0%, transparent 85%)',
              opacity: 0.4
            }}
          />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(165deg, var(--color-bg-primary) 0%, var(--color-bg-secondary) 100%)'
            }}
          />
          <div
            className="absolute -top-32 -left-24 h-[34rem] w-[34rem] rounded-full"
            style={{ background: 'var(--color-primary)', opacity: 0.14, filter: 'blur(110px)' }}
          />
          <div
            className="absolute -bottom-40 -right-24 h-[36rem] w-[36rem] rounded-full"
            style={{ background: 'var(--color-success)', opacity: 0.1, filter: 'blur(120px)' }}
          />
        </>
      )}
      <GrainOverlay />
    </div>
  )
}

/** Thin ornamental divider — line · dot · line. */
export function Divider(): JSX.Element {
  return (
    <div className="flex items-center justify-center gap-3" aria-hidden>
      <span className="h-px w-12 sm:w-16" style={{ background: 'var(--color-primary)', opacity: 0.5 }} />
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--color-primary)', opacity: 0.5 }} />
      <span className="h-px w-12 sm:w-16" style={{ background: 'var(--color-primary)', opacity: 0.5 }} />
    </div>
  )
}

/** Floating pill nav — a rounded, blurred bar that floats over the page and is
 *  used as the header on every storefront/customer page. Brand (left) +
 *  auth action (right): "Sign in" when logged out, "Sign out" when logged in
 *  (customer OR operator). Kiran 2026-05-21. */
export function StorefrontNav({
  onMenu,
  onProfile,
  sidebarInset = false,
  fullWidth = false,
  showShop = false,
  showBuild = false,
  showCart = false,
  searchQuery,
  onSearchChange,
  onFilterToggle
}: {
  /** When set, a mobile-only hamburger renders left of the brand (dashboard). */
  readonly onMenu?: () => void
  /** When set (authed shells), a profile icon renders just before Sign out. */
  readonly onProfile?: () => void
  /** When true, the pill clears a 15rem sidebar on md+ (dashboard). */
  readonly sidebarInset?: boolean
  /** Logged-in shells: span the full width instead of a centred capsule. */
  readonly fullWidth?: boolean
  /** Home page only: a "Shop" link sits left of the auth action. */
  readonly showShop?: boolean
  /** Storefront/account shells: a "Build" pill that enters the
   *  store-builder flow. Gated by `isAuthed` inside the component —
   *  pages can request it freely; signed-out visitors never see it
   *  because /build is for customers only (Kiran 2026-06-06). */
  readonly showBuild?: boolean
  /** Storefront shells (shop/cart): a Cart icon with item-count badge sits
   *  just BEFORE the profile icon. Reads from CartContext. */
  readonly showCart?: boolean
  /** Shop list only: when set, a search input renders inside the pill
   *  (hidden on small screens to keep the pill compact). */
  readonly searchQuery?: string
  readonly onSearchChange?: (next: string) => void
  /** Shop list only: a filter icon button next to search. Click toggles
   *  the sidebar filter panel open/closed (parent owns the state). */
  readonly onFilterToggle?: () => void
} = {}): JSX.Element {
  const [scrolled, setScrolled] = useState(false)
  const reduce = useReducedMotion() === true
  const { brand } = storeConfig
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()
  const tenant = useTenant()
  const profileCtx = useProfileContext()
  const isAuthed = auth.isAuthenticated
  // Customer-side hamburger + slide-in account drawer (Kiran 2026-06-17).
  // Self-contained: opt-in for parents that DON'T already wire onMenu
  // (operator DashboardShell does, so we never collide there). Renders
  // a hamburger left of the brand for signed-in customers + a drawer
  // with Orders History / Returns / Testimony links.
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false)
  const isCustomer = isAuthed && auth.user?.userTypeCode === 'customer'
  const showCustomerHamburger = isCustomer && onMenu === undefined
  // Customer-mode auto-defaults — when signed in as a customer, the
  // Cart icon, Profile glyph (opens the storefront ProfileOverlay), and
  // Build pill should appear on every shell, not just /shop. Per-page
  // explicit props still win when set. Operator pages never trip these
  // because isCustomer === false. Kiran 2026-06-17.
  const effectiveShowCart = showCart || isCustomer
  const effectiveShowBuild = showBuild || isCustomer
  const effectiveOnProfile =
    onProfile !== undefined ? onProfile : isCustomer ? (): void => profileCtx.open() : undefined
  // Search auto-default for customers (Kiran 2026-06-17). When a page
  // already passes searchQuery/onSearchChange (e.g. /shop) those win;
  // otherwise customers get a local input that navigates to /shop?q=…
  // on submit (Enter), so the search bar is reachable from every shell.
  const [internalSearch, setInternalSearch] = useState('')
  const showInlineSearchControlled = searchQuery !== undefined && onSearchChange !== undefined
  const showInlineSearch = showInlineSearchControlled || isCustomer
  const effectiveSearchQuery = showInlineSearchControlled ? searchQuery : internalSearch
  const submitCustomerSearch = (): void => {
    const q = internalSearch.trim()
    navigate(q.length > 0 ? `${ROUTES.SHOP}?q=${encodeURIComponent(q)}` : ROUTES.SHOP)
  }
  // Close the customer drawer on every route change so the next click
  // navigates instead of opening to an already-open drawer.
  useEffect(() => {
    if (customerDrawerOpen) setCustomerDrawerOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])
  // Esc + scroll-lock parity with DashboardShell.
  useEffect(() => {
    if (!customerDrawerOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setCustomerDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [customerDrawerOpen])
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 24)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const handleSignOut = (): void => {
    void (async (): Promise<void> => {
      if (auth.isAuthenticated) await auth.logout()
      navigate(ROUTES.HOME)
    })()
  }
  const motionInteract = reduce ? {} : { whileHover: { scale: 1.05 }, whileTap: { scale: 0.95 } }
  const actionClasses =
    'relative z-10 group flex flex-shrink-0 items-center gap-1.5 rounded-full border border-forest/30 bg-forest/10 px-4 py-1.5 font-body text-body-sm text-charcoal shadow-sm transition-colors duration-200 hover:border-forest hover:bg-forest hover:shadow-md whitespace-nowrap'
  return (
    <>
    {showCustomerHamburger && customerDrawerOpen ? (
      <div
        aria-hidden
        className="fixed inset-0 z-30 bg-charcoal/40"
        onClick={() => setCustomerDrawerOpen(false)}
      />
    ) : null}
    {showCustomerHamburger ? <CustomerSidebar drawerOpen={customerDrawerOpen} /> : null}
    <motion.header
      className={`fixed inset-x-0 top-3 z-40 px-3 sm:top-5 ${sidebarInset ? 'md:pl-60' : ''}`}
      initial={reduce ? false : { y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        className={`relative flex items-center justify-between gap-3 rounded-full border border-border/50 px-3 py-2 ring-1 ring-white/30 backdrop-blur-md transition-shadow duration-300 sm:px-5 ${
          fullWidth ? 'w-full' : 'mx-auto max-w-3xl'
        } ${scrolled ? 'bg-bg-primary/85 shadow-lg' : 'bg-bg-primary/70 shadow-md'}`}
      >
        {/* Glass sheen + a one-time light glint sweeping across on load. */}
        <span aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-full">
          <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent" />
          {reduce ? null : (
            <motion.span
              className="absolute inset-y-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/35 to-transparent"
              initial={{ x: '-160%' }}
              animate={{ x: '460%' }}
              transition={
                isAuthed
                  ? { duration: 1.4, ease: 'easeInOut', repeat: Infinity, repeatDelay: 5, delay: 0.45 }
                  : { duration: 1.1, ease: 'easeInOut', delay: 0.45 }
              }
            />
          )}
        </span>
        <div className="relative z-10 flex min-w-0 items-center gap-2">
          {onMenu !== undefined || showCustomerHamburger ? (
            <button
              type="button"
              onClick={onMenu ?? (() => setCustomerDrawerOpen((v) => !v))}
              aria-label="Open menu"
              className="-ml-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-charcoal transition hover:bg-forest/15 hover:text-forest"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          ) : null}
          <div className="flex min-w-0 items-center gap-2">
            {/* Brand wordmark — plain text, NOT a link (Kiran 2026-05-21). */}
            <span className="flex flex-shrink-0 font-heading text-body-lg text-charcoal leading-none">
              {brand.name.split('').map((ch, i) => (
                <motion.span
                  key={i}
                  className="inline-block"
                  initial={reduce ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, ease: 'easeOut', delay: 0.42 + i * 0.04 }}
                >
                    {ch === ' ' ? ' ' : ch}
                </motion.span>
              ))}
            </span>
            {/* Filter toggle sits NEXT TO the brand on the left and is
                always visible on the shop list (Kiran 2026-06-05). */}
            {onFilterToggle !== undefined ? (
              <motion.button
                type="button"
                onClick={onFilterToggle}
                {...motionInteract}
                aria-label="Toggle filters"
                title="Toggle filters"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-forest/30 bg-forest/10 text-charcoal shadow-sm transition-colors duration-200 hover:border-forest hover:bg-forest hover:shadow-md"
              >
                <FilterGlyph />
              </motion.button>
            ) : null}
          </div>
        </div>
        {/* Shop-list middle slot: search input only — the filter
            toggle moved next to the brand on the left (2026-06-05).
            Search is always visible across breakpoints. */}
        {showInlineSearch ? (
          <div className="relative z-10 flex min-w-0 flex-1 items-center">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-full border border-border/50 bg-bg-primary/60 px-2 py-1.5 text-charcoal sm:px-3">
              <SearchGlyph />
              <input
                type="search"
                value={effectiveSearchQuery}
                onChange={(e) => {
                  if (showInlineSearchControlled && onSearchChange !== undefined) {
                    onSearchChange(e.target.value)
                  } else {
                    setInternalSearch(e.target.value)
                  }
                }}
                onKeyDown={(e) => {
                  if (!showInlineSearchControlled && e.key === 'Enter') {
                    e.preventDefault()
                    submitCustomerSearch()
                  }
                }}
                placeholder="Search products"
                className="w-full min-w-0 bg-transparent font-body text-body-sm placeholder:text-text-secondary focus:outline-none"
                aria-label="Search products"
              />
            </div>
          </div>
        ) : null}
        <div className="relative z-10 flex flex-shrink-0 items-center gap-2">
          {showShop ? (
            <motion.div {...motionInteract} className="flex-shrink-0">
              <Link to={ROUTES.SHOP} className={actionClasses}>
                <ShopGlyph />
                Shop
              </Link>
            </motion.div>
          ) : null}
          {effectiveShowBuild && isAuthed && tenant.isMaster ? (
            // /build is the upstream Uyyamadilu store-builder flow —
            // hidden on tenant subdomains where the visitor is a Magna
            // customer, not a Uyyamadilu customer (Phase 3, Kiran
            // 2026-06-07).
            <motion.div {...motionInteract} className="flex-shrink-0">
              <Link to={ROUTES.BUILD} className={actionClasses}>
                <BuildGlyph />
                Build
              </Link>
            </motion.div>
          ) : null}
          {effectiveShowCart ? <CartNavButton /> : null}
          {isAuthed ? (
            <>
              {effectiveOnProfile !== undefined ? (
                <motion.button
                  type="button"
                  onClick={effectiveOnProfile}
                  {...motionInteract}
                  aria-label="My profile"
                  title="My profile"
                  className="group flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-forest/30 bg-forest/10 text-charcoal shadow-sm transition-colors duration-200 hover:border-forest hover:bg-forest hover:shadow-md"
                >
                  <ProfileGlyph />
                </motion.button>
              ) : null}
              <motion.button type="button" onClick={handleSignOut} {...motionInteract} className={actionClasses}>
                <AuthGlyph out />
                Sign out
              </motion.button>
            </>
          ) : (
            <motion.div {...motionInteract} className="flex-shrink-0">
              {/* state.from preserves the storefront URL through login so
                  the customer lands back where they were (e.g. /build →
                  /login → /build), keeping their per-surface cart in
                  view. Kiran 2026-06-06. */}
              <Link to={ROUTES.LOGIN} state={{ from: location.pathname }} className={actionClasses}>
                <AuthGlyph out={false} />
                Sign in
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </motion.header>
    </>
  )
}

/** Person glyph for the profile icon button (head + shoulders). */
function ProfileGlyph(): JSX.Element {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="text-forest transition-colors duration-200 group-hover:text-charcoal"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </svg>
  )
}

/** Magnifier glyph for the storefront search input. */
function SearchGlyph(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="text-text-secondary">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}

/** Funnel glyph for the filter toggle button. */
function FilterGlyph(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="text-forest transition-colors duration-200 group-hover:text-charcoal">
      <path d="M3 5h18l-7 9v6l-4-2v-4z" />
    </svg>
  )
}

/**
 * Cart link with an item-count badge — sits between Shop and Profile on the
 * storefront pages (via `showCart`). Reads CartContext so the badge stays
 * in sync with every add / update / remove without prop-drilling.
 */
function CartNavButton(): JSX.Element {
  const { cart, open } = useCartContext()
  const count = cart.item_count
  const reduce = useReducedMotion() === true
  const motionInteract = reduce ? {} : { whileHover: { scale: 1.05 }, whileTap: { scale: 0.95 } }
  return (
    <motion.div {...motionInteract} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={open}
        aria-label={count > 0 ? `Open cart, ${count} item${count === 1 ? '' : 's'}` : 'Open cart'}
        title="Cart"
        className="group flex h-9 w-9 items-center justify-center rounded-full border border-forest/30 bg-forest/10 text-charcoal shadow-sm transition-colors duration-200 hover:border-forest hover:bg-forest hover:shadow-md"
      >
        <CartGlyph />
      </button>
      {count > 0 ? (
        <span
          aria-hidden
          className="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-forest px-1 font-mono text-[10px] font-bold leading-none text-text-light shadow"
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </motion.div>
  )
}

/** Shopping-cart glyph for the cart nav button (distinct from the Shop bag). */
function CartGlyph(): JSX.Element {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="text-forest transition-colors duration-200 group-hover:text-charcoal"
    >
      <circle cx="8" cy="21" r="1" />
      <circle cx="19" cy="21" r="1" />
      <path d="M2 3h2.5l2.5 12a2 2 0 0 0 2 1.5h9a2 2 0 0 0 2-1.5l1.5-7H6" />
    </svg>
  )
}

/** Wrench-on-blocks glyph for the "Build" nav action — signals the
 *  store-builder flow (compose products → publish your own sub-domain).
 *  Visually distinct from the Shop bag so the two actions read at a
 *  glance. */
function BuildGlyph(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="text-forest transition-colors duration-200 group-hover:text-charcoal"
    >
      <rect x="3" y="13" width="7" height="7" rx="1" />
      <rect x="14" y="13" width="7" height="7" rx="1" />
      <rect x="8.5" y="3" width="7" height="7" rx="1" />
    </svg>
  )
}

/** Storefront/bag glyph for the "Shop" nav action. */
function ShopGlyph(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="text-forest transition-colors duration-200 group-hover:text-charcoal"
    >
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  )
}

/** Small login / logout glyph (door + arrow) for the nav action. */
function AuthGlyph({ out }: { readonly out: boolean }): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="text-forest transition-colors duration-200 group-hover:text-charcoal"
    >
      {out ? (
        <>
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="M16 17l5-5-5-5" />
          <path d="M21 12H9" />
        </>
      ) : (
        <>
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <path d="M10 17l5-5-5-5" />
          <path d="M15 12H3" />
        </>
      )}
    </svg>
  )
}

/** Brand lockup for the footer — Kannada wordmark + English name, on dark. */
function BrandLockup(): JSX.Element {
  const { name, nameScript } = storeConfig.brand
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      {nameScript !== null ? (
        <span className="font-kannada text-h3 text-text-light leading-none">{nameScript}</span>
      ) : null}
      <span className="font-heading text-body-lg text-text-light leading-none">{name}</span>
    </span>
  )
}

/** Warm-dark footer. */
export function SiteFooter(): JSX.Element {
  const { tagline, contact, social, name } = storeConfig.brand
  const year = new Date().getFullYear()
  return (
    <footer className="bg-bg-dark text-text-light">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <BrandLockup />
            <p className="mt-2 font-heading italic" style={{ opacity: 0.85, fontSize: '0.95rem' }}>
              {tagline}
            </p>
          </div>
          <div className="font-body text-body-sm" style={{ opacity: 0.85 }}>
            {contact.address !== null ? <p className="max-w-xs leading-relaxed">{contact.address}</p> : null}
            {social.instagram !== null ? (
              <a href={social.instagram} className="mt-2 inline-block underline-offset-4 hover:underline">
                Instagram
              </a>
            ) : null}
          </div>
        </div>
        <div
          className="mt-10 border-t pt-5 font-body text-micro"
          style={{ borderColor: 'rgba(255,255,255,0.2)', opacity: 0.7 }}
        >
          © {year} {name} · Bengaluru, India
        </div>
      </div>
    </footer>
  )
}
